import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import PerformanceBoardSlideOutContext from '../contexts/PerformanceBoardSlideOutContext';
import { Z_INDEX } from '../../../constants';

const DURATION = 300;
export const SLIDE_OUT_WIDTH = 440;
export const SLIDE_OUT_WIDTH_WIDE = 640;

/**
 * Resolves the slideout panel width for a board. Kept as the single source of
 * truth so the panel (here) and the grid layout stay in sync. Widen the map as
 * new `slideoutWidth` options are added.
 */
export const getSlideOutWidth = (
  board: PerformanceBoardTypes.Board,
): number => {
  switch (board.slideoutWidth) {
    case 'wide':
      return SLIDE_OUT_WIDTH_WIDE;
    default:
      return SLIDE_OUT_WIDTH;
  }
};

const SlideOutDiv = styled(motion.div)<{ $width: number }>`
  height: 100%;
  background-color: var(--color-bg-primary);
  z-index: ${Z_INDEX.SLIDE_OUT};
  position: absolute;
  width: ${(props) => props.$width}px;
  right: 0px;
  box-shadow: var(--shadow-md);
  border-radius: 2px;
  z-index: 1;
  border: 1px solid var(--color-border-secondary);
`;

const getVariants = (width: number) => ({
  opening: {
    right: -width,
  },
  open: {
    right: 0,
  },
  closing: {
    right: -width,
  },
  closed: {
    right: -width,
  },
});

const SlideOut = ({
  children,
}: {
  children: JSX.Element | JSX.Element[] | false;
}) => {
  const { isOpen, board } = useContext(PerformanceBoardSlideOutContext);
  const [isCloseComplete, setIsCloseComplete] = useState<boolean>(true);

  const width = getSlideOutWidth(board);
  const variants = getVariants(width);

  useEffect(() => {
    if (isOpen) {
      setIsCloseComplete(false);
      return;
    }
    const t = setTimeout(() => {
      setIsCloseComplete(true);
    }, DURATION);

    return () => {
      clearTimeout(t);
    };
  }, [isOpen]);

  if (isCloseComplete) {
    return null;
  }

  return (
    <SlideOutDiv
      $width={width}
      variants={variants}
      animate={isOpen ? 'open' : isCloseComplete ? 'closed' : 'closing'}
      initial="opening"
    >
      {children}
    </SlideOutDiv>
  );
};

export default SlideOut;
